import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UserFavAppService, UserFavApp } from '../../osm-services/user-fav-app.service';
import { AppUsageService } from '../../osm-services/app-usage.service';
import { AuthService } from '../../services/auth.service';
import { Subscription } from 'rxjs';
import { ImageUtils } from '../../shared/utils/image.utils';
import { CategoryUtils } from '../../shared/utils/category.utils';

@Component({
  selector: 'ozm-my-favorites',
  templateUrl: './my-favorites.component.html',
  styleUrls: ['./my-favorites.component.scss']
})
export class MyFavoritesComponent implements OnInit, OnDestroy {
  userFavoriteApps: UserFavApp[] = [];
  loading: boolean = false;
  private subscription: Subscription = new Subscription();
  private userId: string = '';
  
  constructor(
    private userFavAppService: UserFavAppService,
    private appUsageService: AppUsageService,
    private authService: AuthService,
    private router: Router
  ) { }

  // Utility methods for consistent image handling
  getAppIcon(app: any): string {
    return ImageUtils.getAppIcon(app);
  }



  getDisplayCategoryName(app: any): string {
    return CategoryUtils.getCategoryDisplayName(app.category || app.categoryName, []);
  }

  getDisplaySubcategoryName(app: any): string {
    return CategoryUtils.getSubcategoryDisplayName(app.subCategory || app.subCategoryName, []);
  }

  ngOnInit(): void {
    // Get user ID
    this.authService.getUser().then(user => {
      if (user && user.attributes) {
        this.userId = user.attributes.sub;
      }
    });
    
    this.loadFavoriteApps();
    
    // Subscribe to real-time updates
    this.subscription.add(
      this.userFavAppService.userFavApps$.subscribe(apps => {
        this.userFavoriteApps = apps;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadFavoriteApps(): void {
    this.loading = true;
    this.userFavAppService.getUserFavApps().subscribe({
      next: (response) => {
        this.userFavoriteApps = response.apps || [];
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading favorite apps:', error);
        this.loading = false;
      }
    });
  }

  removeFromFavorites(app: UserFavApp): void {
    if (confirm(`Are you sure you want to remove "${app.name}" from your favorites?`)) {
      this.loading = true;
      this.userFavAppService.removeFromFavorites(app.appliId).subscribe({
        next: () => {
          this.loading = false;
          // The list will be updated automatically via the subscription
        },
        error: (error) => {
          console.error('Error removing app from favorites:', error);
          this.loading = false;
          alert(`Failed to remove ${app.name} from favorites`);
        }
      });
    }
  }

  openApp(app: UserFavApp): void {
    // Track app usage if user is available
    if (this.userId) {
      this.appUsageService.trackAppUsage({
        userId: this.userId,
        appId: app.appliId,
        appName: app.name,
        category: app.category,
        subCategory: app.subCategory
      }).subscribe({
        next: (response) => {
          console.log('App usage tracked:', response);
        },
        error: (error) => {
          console.error('Error tracking app usage:', error);
        }
      });
    }
    
    if (app.link) {
      window.open(app.link, '_blank');
    }
  }

  navigateToCategories(): void {
    this.router.navigate(['/category']);
  }

  // Group apps by category for better organization
  getAppsByCategory(): { [key: string]: UserFavApp[] } {
    return this.userFavoriteApps.reduce((grouped, app) => {
      const category = app.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(app);
      return grouped;
    }, {} as { [key: string]: UserFavApp[] });
  }

  getCategoryNames(): string[] {
    return Object.keys(this.getAppsByCategory()).sort();
  }

  onImageError(event: any): void {
    ImageUtils.handleImageError(event);
  }
}
